import { Decorative, Core } from "@faire/design-tokens";
import { trackSamplesGuestRetailerConfirmationModalUpsellClick } from "@faire/web-api/events/samples/click/guestRetailerConfirmationModalUpsell";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import SignUpPersonalRoute, {
  QueryParameters as SignUpQueryParameters,
} from "@faire/web-api/routes/welcome/r/personal";
import { Button } from "@faire/web/slate/Button";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useQueryParams } from "@faire/web/ui/hooks/useQueryParams";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import * as React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { useFaireDirectBrand } from "@faire/retailer/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { useFaireDirectFirstOrderIncentive } from "@faire/retailer/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import { BannerPicture } from "@faire/retailer/views/brand/BrandHeader/BannerPicture";

interface GuestRetailerConfirmationModalProps {
  bannerImage: IImage | undefined;
  name: string;
  email: string;
  phoneNumber: string;
  storeName: string;
  country: string;
  googlePlaceId: string;
  onLearnMoreClick: () => void;
}

// We have a special FD extension only for sampling retailers
const SAMPLE_FD_EXPIRY_EXTENSION_DAYS = 30;

export const GuestRetailerConfirmationModal = React.forwardRef<
  HTMLDivElement,
  GuestRetailerConfirmationModalProps
>((props, ref) => {
  return (
    <StyledColumn ref={ref}>
      <Typography variant="displaySSerifRegular">
        <StrictLocalMsg
          defaultMessage="We got your request"
          description={{
            text: "Header of the sample request confirmation modal",
            img: "https://cdn.faire.com/fastly/474e5bba4fea677da3ee835f5b07373b56fdc0ed5db52093df524a3f66baabc6.png",
          }}
        />
      </Typography>
      <StyledTypography>
        <StrictLocalMsg
          defaultMessage="We'll start working on your sample right away. Bon appétit!"
          description={{
            text: "Sub-heading, explaining that the sample request will be processed right away",
            img: "https://cdn.faire.com/fastly/474e5bba4fea677da3ee835f5b07373b56fdc0ed5db52093df524a3f66baabc6.png",
          }}
        />
      </StyledTypography>
      <UpsellCard {...props} />
    </StyledColumn>
  );
});

const UpsellCard: React.FC<GuestRetailerConfirmationModalProps> = ({
  bannerImage,
  name,
  email,
  phoneNumber,
  storeName,
  country,
  googlePlaceId,
  onLearnMoreClick,
}) => {
  const { faireDirectBrandAlias } = useFaireDirectBrand();
  const { fdb } = useQueryParams(SignUpQueryParameters.parse);
  const { replace } = useHistory();

  const {
    isFaireDirectCreditPercentageBased,
    formattedPercentageBasedNumber,
    formattedMaxPercentageDiscountAmount,
    formattedFixedBasedAmount,
  } = useFaireDirectFirstOrderIncentive();

  const goToSignUp = () => {
    trackSamplesGuestRetailerConfirmationModalUpsellClick();
    replace(
      SignUpPersonalRoute(
        SignUpQueryParameters.build({
          name,
          email,
          storeName,
          googlePlaceId,
          fdb: fdb ?? faireDirectBrandAlias,
          countryIso3: country,
          phone_number: phoneNumber,
        })
      )
    );
  };

  return (
    <CardColumn>
      <BannerContainer>
        <BannerPicture brandImage={bannerImage} />
      </BannerContainer>
      <PaddedContent>
        <Typography
          variant="paragraphSansRegular"
          color={Decorative.icon.orange}
        >
          <StrictLocalMsg
            defaultMessage="Special offer"
            description={{
              text: "a badge highlighting that this card contains a special offer",
              img: "https://cdn.faire.com/fastly/474e5bba4fea677da3ee835f5b07373b56fdc0ed5db52093df524a3f66baabc6.png",
            }}
          />
        </Typography>
        <Typography variant="pageHeaderSerifRegular">
          {isFaireDirectCreditPercentageBased ? (
            <StrictLocalMsg
              defaultMessage="Place a full order and get {faireDirectPercentageDiscountNumber} off & 1 year of free shipping*"
              description={{
                text: "Faire Direct offer",
                img: "https://cdn.faire.com/fastly/d624118b9b528af0e61cd5932d01a18d9a9cd63ca76a876d2ba20868f8c420ae.png",
              }}
              values={{
                faireDirectPercentageDiscountNumber:
                  formattedPercentageBasedNumber,
              }}
            />
          ) : (
            <StrictLocalMsg
              defaultMessage="Place a full order and get {promoMoney} off & 1 year of free shipping*"
              description={{
                text: "Faire Direct offer",
                img: "https://cdn.faire.com/fastly/474e5bba4fea677da3ee835f5b07373b56fdc0ed5db52093df524a3f66baabc6.png",
              }}
              values={{
                promoMoney: formattedFixedBasedAmount,
              }}
            />
          )}
        </Typography>
        <CardBodyTypography>
          {isFaireDirectCreditPercentageBased ? (
            <StrictLocalMsg
              defaultMessage="Join Faire today and get {faireDirectPercentageDiscountNumber} off your first order and 1 year of free shipping with us."
              description={{
                text: "explanation for the Faire Direct offer",
                img: "https://cdn.faire.com/fastly/d624118b9b528af0e61cd5932d01a18d9a9cd63ca76a876d2ba20868f8c420ae.png",
              }}
              values={{
                faireDirectPercentageDiscountNumber:
                  formattedPercentageBasedNumber,
              }}
            />
          ) : (
            <StrictLocalMsg
              defaultMessage="Join Faire today and get {promoMoney} off your first order and 1 year of free shipping with us."
              description={{
                text: "explanation for the Faire Direct offer",
                img: "https://cdn.faire.com/fastly/474e5bba4fea677da3ee835f5b07373b56fdc0ed5db52093df524a3f66baabc6.png",
              }}
              values={{
                promoMoney: formattedFixedBasedAmount,
              }}
            />
          )}
        </CardBodyTypography>
        <CtaContainer>
          <Button onClick={goToSignUp}>
            <StrictLocalMsg
              defaultMessage="Create account & claim offer"
              description={{
                text: "button that redirects to the signup page",
                img: "https://cdn.faire.com/fastly/474e5bba4fea677da3ee835f5b07373b56fdc0ed5db52093df524a3f66baabc6.png",
              }}
            />
          </Button>
        </CtaContainer>
        <Column align="flex-start">
          <Button variant="tertiary" onClick={onLearnMoreClick}>
            <Typography variant="paragraphSansRegular">
              <StrictLocalMsg
                defaultMessage="Learn more about Faire"
                description={{
                  text: "link that closes this modal and shows Faire Direct landing page",
                  img: "https://cdn.faire.com/fastly/474e5bba4fea677da3ee835f5b07373b56fdc0ed5db52093df524a3f66baabc6.png",
                }}
              />
            </Typography>
          </Button>
          <Spacer height="4x" />
          <Typography variant="paragraphSansRegular" color={Core.text.subdued}>
            {isFaireDirectCreditPercentageBased ? (
              <StrictLocalMsg
                defaultMessage="*Max discount of {faireDirectMaxDiscountNumber}. Expires {expiresInDaysNumber} days after signup."
                description={{
                  text: "Disclaimer for the Faire Direct signup credit",
                  img: "https://cdn.faire.com/fastly/d624118b9b528af0e61cd5932d01a18d9a9cd63ca76a876d2ba20868f8c420ae.png",
                }}
                values={{
                  faireDirectMaxDiscountNumber:
                    formattedMaxPercentageDiscountAmount,
                  expiresInDaysNumber: SAMPLE_FD_EXPIRY_EXTENSION_DAYS,
                }}
              />
            ) : (
              <StrictLocalMsg
                defaultMessage="*Expires {expiresInDaysNumber} days after signup."
                description={{
                  text: "Disclaimer for the Faire Direct signup credit",
                  img: "https://cdn.faire.com/fastly/03e4a00ae494f6b2adbc6b0ff4c394913f76802152a954c76e339193c66e643c.png",
                }}
                values={{
                  expiresInDaysNumber: SAMPLE_FD_EXPIRY_EXTENSION_DAYS,
                }}
              />
            )}
          </Typography>
        </Column>
      </PaddedContent>
    </CardColumn>
  );
};

const StyledColumn = styled(Column).attrs({
  gap: "1x",
  align: "center",
})`
  width: 500px;
  padding: ${getSpacing("2x", 0, 0)};
  text-align: center;

  ${responsiveMediaQueries.mobile} {
    width: 350px;
  }
`;

const CardColumn = styled(Column).attrs({
  align: "flex-start",
})`
  background: ${Core.surface.inverse};
  border-radius: 10px;
  overflow: hidden;
  margin-top: ${getSpacing("6x")};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  text-align: left;
`;

const StyledTypography = styled(Typography).attrs({
  variant: "paragraphSansRegular",
})`
  text-align: center;
`;

const CardBodyTypography = styled(Typography).attrs({
  variant: "paragraphSansRegular",
})`
  &&& {
    padding: ${getSpacing("1x", 0)};
  }
`;

const PaddedContent = styled(Column).attrs({
  gap: "1x",
})`
  padding: ${getSpacing("2x", "3x", "3x")};
`;

const BannerContainer = styled(Column)`
  height: 110px;
`;

const CtaContainer = styled(Column).attrs({
  align: "flex-start",
})`
  margin: ${getSpacing("1x", 0)};
`;
